import React, {
	type ClipboardEvent,
	useEffect,
	useState,
	useCallback,
	useMemo,
	type KeyboardEvent,
	type ElementRef,
} from 'react';
import noop from 'lodash/noop';
import { Flex, xcss } from '@atlaskit/primitives';
import type { CategorySelectOption } from '@atlassian/jira-business-categories/src/common/types.tsx';
import { useCategoryCellStateController } from '@atlassian/jira-business-categories/src/controllers/category-cell-state-controller/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	CREATE_CATEGORY_EXPERIENCE,
	UPDATE_CATEGORY_EXPERIENCE,
	DELETE_CATEGORY_EXPERIENCE,
} from '../../../common/constants.tsx';
import { CellContainer } from '../../../common/ui/list-cell/styled.tsx';
import { CategoryCellContent } from './category-cell-content/index.tsx';
import DeleteCategoryModal from './delete-category-modal/index.tsx';
import { EditCategoryCell } from './edit-category-cell/index.tsx';
import messages from './messages.tsx';
import type { CategoryCellProps } from './types.tsx';

type CategoryCellState = { status: 'read-mode' } | { status: 'edit-mode' };

const CategoryCell = ({
	isEditable,
	fieldKey,
	fieldValue,
	issueKey,
	width,
	fieldType,
	issueTypeId,
	isSelected = false,
	wrapperRef,
	onFocus,
	onBlur,
	onKeyDown,
	fieldName,
	editMode = false,
	setEditMode = noop,
	cellRef,
	issueType = '',
	inputValue,
	onInputChange,
}: CategoryCellProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated categoryCellState is now editMode which is controlled by CellNavigation
	 */
	const [categoryCellState, setCategoryCellState] = useState<CategoryCellState>({
		status: 'read-mode',
	});
	const [categoryToDeleteInModal, setCategoryToDeleteInModal] =
		useState<CategorySelectOption | null>(null);

	const {
		editFieldError,
		categoryState,
		createAndAssignNewCategory,
		updateCategory,
		deleteAndUnassignCategory,
		categoryOptions,
		assignCategoryToCell,
	} = useCategoryCellStateController({
		fieldValue,
		fieldType,
		issueKey,
		fieldKey,
		issueTypeId,
		createExperience: CREATE_CATEGORY_EXPERIENCE,
		updateExperience: UPDATE_CATEGORY_EXPERIENCE,
		deleteExperience: DELETE_CATEGORY_EXPERIENCE,
	});

	const project = useProject();

	const onClose = useCallback(() => {
		if (fg('jira_list_keyboard_nav')) {
			setEditMode(false);
		} else {
			setCategoryCellState({ status: 'read-mode' });
		}
	}, [setEditMode]);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const handleClick = useCallback(() => {
		if (isEditable && categoryCellState.status === 'read-mode') {
			setCategoryCellState({ status: 'edit-mode' });
		}
	}, [categoryCellState.status, isEditable]);

	const handleCutCopy = useCallback(
		(event: ClipboardEvent<HTMLElement>) => {
			event.preventDefault();
			if (categoryState?.value) {
				event.clipboardData.setData('text/plain', categoryState?.value);
			}

			if (event.type === 'copy') {
				fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'category');
			} else if (event.type === 'cut') {
				fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'category');
				// clear cell content
				assignCategoryToCell(null);
			}
		},
		[categoryState?.value, createAnalyticsEvent, assignCategoryToCell],
	);

	const handlePaste = useCallback(
		(event: ClipboardEvent<HTMLElement>) => {
			const clipText = event.clipboardData.getData('text/plain');
			const foundCategory = categoryOptions.find((option) => option.label === clipText);
			if (foundCategory) {
				fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'category', {
					valid: true,
				});
			} else {
				fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'category', {
					valid: false,
					error: 'Not a valid category',
				});
			}
		},
		[createAnalyticsEvent, categoryOptions],
	);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const onWrapperRef = useCallback(
		(ref: ElementRef<'div'> | null) => {
			if (wrapperRef) {
				// eslint-disable-next-line no-param-reassign
				wrapperRef.current = ref;
			}
		},
		[wrapperRef],
	);

	const onDeleteCompleted = useCallback(() => {
		setCategoryToDeleteInModal(null);
	}, [setCategoryToDeleteInModal]);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const onEscapeKeyPress = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			e.stopPropagation();
			setCategoryCellState({ status: 'read-mode' });
			wrapperRef?.current?.focus();
		},
		[wrapperRef],
	);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Escape') {
				onEscapeKeyPress(e);
			} else {
				categoryCellState.status === 'read-mode' && onKeyDown?.(e);
			}
		},
		[categoryCellState.status, onEscapeKeyPress, onKeyDown],
	);

	// we need to sort the options so that the selected category is at the top of the list
	// as the first option is hovered by default and will be selected if `Enter` is pressed
	// immediately after the select dropdown is shown
	const sortedCategories = useMemo<CategorySelectOption[]>(() => {
		if (categoryState == null) {
			return categoryOptions;
		}

		const selectedCategoryIndex = categoryOptions.findIndex(
			(option) => option.value === categoryState?.optionId,
		);

		if (selectedCategoryIndex === -1) {
			return categoryOptions;
		}

		const sortedCategoryOptions = [...categoryOptions];
		const selectedCategoryOption = sortedCategoryOptions[selectedCategoryIndex];

		sortedCategoryOptions.splice(selectedCategoryIndex, 1);
		sortedCategoryOptions.unshift(selectedCategoryOption);
		return sortedCategoryOptions;
	}, [categoryOptions, categoryState]);

	const triggerContent = useMemo(
		() => <CategoryCellContent isEditable={isEditable} categoryState={categoryState} />,
		[isEditable, categoryState],
	);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Now handled in CellNavigation component
	 */
	useEffect(() => {
		// Syncing the `isSelected` to `categoryCellState`
		// Scenario where is this needed
		// - if the current cell is in `edit-mode` (i.e.`EditCategoryCell` is mounted)
		// - when user clicks on another category cell
		// - this will unmount the `EditCategoryCell` so there will be no multiple pop ups being opened
		//
		// For some reason the `onClose` is not called when user clicks on another category cell
		// only close the edit mode if the deleteModal is not open
		if (
			isSelected === false &&
			categoryCellState.status === 'edit-mode' &&
			!categoryToDeleteInModal &&
			!fg('jira_list_keyboard_nav')
		) {
			setCategoryCellState({ status: 'read-mode' });
		}
	}, [isSelected, categoryToDeleteInModal, categoryCellState.status]);

	const tmpError = project.isSimplified && editFieldError;

	const staticContent = fg('jira_list_keyboard_nav') ? triggerContent : <div>{triggerContent}</div>;

	const cellContent = (
		<>
			{((editMode || categoryToDeleteInModal) && fg('jira_list_keyboard_nav')) ||
			(categoryCellState.status === 'edit-mode' && !fg('jira_list_keyboard_nav')) ? (
				<EditCategoryCell
					width={width}
					issueTypeId={issueTypeId}
					categoryOptions={sortedCategories}
					triggerContent={triggerContent}
					onClose={onClose}
					wrapperRef={wrapperRef}
					setCategoryToDeleteInModal={setCategoryToDeleteInModal}
					categoryState={categoryState}
					onCategoryCreate={createAndAssignNewCategory}
					onCategoryCellValueChange={assignCategoryToCell}
					onCategoryUpdate={updateCategory}
					cellRef={fg('jira_list_keyboard_nav') ? cellRef : undefined}
					inputValue={fg('jira_list_keyboard_nav') ? inputValue : undefined}
					onInputChange={fg('jira_list_keyboard_nav') ? onInputChange : undefined}
				/>
			) : (
				staticContent
			)}
			{categoryToDeleteInModal && (
				<DeleteCategoryModal
					categoryToDelete={categoryToDeleteInModal}
					onDeleteCompleted={onDeleteCompleted}
					onCategoryDelete={deleteAndUnassignCategory}
				/>
			)}
			{tmpError && (
				<FixItErrorFlag
					error={editFieldError}
					title={messages.errorTitle}
					description={messages.errorMessage}
					issueType={issueType}
					issueTypeId={String(issueTypeId)}
					fieldId={fieldKey}
					fieldName={fieldName}
				/>
			)}
		</>
	);

	if (fg('jira_list_keyboard_nav')) {
		return (
			<CellWrapper
				isEditable={isEditable}
				hasPadding={false}
				testId="business-list.ui.list-view.category-cell.cell-container"
				{...(!fg('jwm_list_new_copy_paste_behaviour')
					? {
							onCopy: handleCutCopy,
							onCut: handleCutCopy,
							onPaste: handlePaste,
						}
					: {})}
			>
				<Flex xcss={cellContentStyles}>{cellContent}</Flex>
			</CellWrapper>
		);
	}

	return (
		<CellContainer
			onCopy={handleCutCopy}
			onCut={handleCutCopy}
			onPaste={handlePaste}
			onClick={handleClick}
			isFocused={isSelected && categoryCellState.status === 'read-mode'}
			onBlur={(event: React.FocusEvent<HTMLElement>) => {
				/**
				 * This function will call `handleBlur` in `list-view/cell-navigation/index.tsx`
				 * - Which in turn will cause `isSelected` to be `false` in here
				 * - We don't want that because when user is in `edit-mode`,
				 *   onBlur can be called when user clicks on the Popup or Dropdown that is rendered by this cell
				 *
				 * - If user clicks on Popup or Dropdown that is part of this cell, we should retain the `isSelected` to be `true`
				 *
				 * See https://hello.atlassian.net/wiki/spaces/JBus/pages/2983695106/Tech+Debugging+category+colour+in+list+view+with+cell-navigation for more details
				 */
				if (categoryCellState.status === 'read-mode') {
					onBlur?.(event);
				}
			}}
			tabIndex={0}
			onFocus={onFocus}
			onKeyDown={handleKeyDown}
			ref={onWrapperRef}
			data-testid="business-list.ui.list-view.category-cell.cell-container"
		>
			{cellContent}
		</CellContainer>
	);
};

export default CategoryCell;

const cellContentStyles = xcss({ width: '100%', height: '100%' });
